<template>
  <div ref="selectorRef" class="source-person-selector">
    <div v-if="showSearchInputRef" class="edit-mode">
      <SearchInput ref="searchInputRef"
        v-model="searchTextRef"
        :search-results="searchResultsRef"
        :placeholder="placeholderRef"
        v-slot="{ item }"
        @focus="emit('focus')"
        @result-selected="searchResultSelected">
        <div>
          {{ getPerson(item.id)?.displayName }}
          <span v-if="getPerson(item.id)?.displayProperties.birthYear" class="birth-year">
            ({{ getPerson(item.id)?.displayProperties.birthYear }})
          </span>
        </div>
      </SearchInput>
    </div>
    <div v-else class="selected-person">
      <button type="button" ref="removeButtonRef" class="btn btn-inline btn-link" :disabled="disabled" @click.stop="onEdit">
        {{ selectedPersonRef?.displayName ?? "Select a person" }}
        <span v-if="selectedPersonRef?.displayProperties.birthYear" class="birth-year">
          ({{ selectedPersonRef?.displayProperties.birthYear }})
        </span>
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.source-person-selector {
  display: flex;
  align-items: center;
  min-height: 38px;

  .edit-mode,
  .selected-person {
    flex: 1;
  }
}

.selected-person {
  display: flex;
  gap: 0.35rem;
  align-items: center;

  button {
    font-size: inherit;
  }

  button[disabled] {
    color: #aaa;
    opacity: 1;
  }
}

.birth-year {
  font-size: 0.875em;
}
</style>

<script lang="ts" setup>
import { computed, nextTick, onUnmounted, ref } from 'vue'
import { onClickOutside } from '@vueuse/core'
import { Person } from '@webapp/rd/ResearchDataModel'
import { useLiveSearch } from '@webapp/util/LiveSearch'
import { useSyncSessionStore } from './SyncSessionStore'
import SearchInput from '@webapp/util/SearchInput.vue'

const props = defineProps<{
  sessionId: string
  modelValue?: string
  placeholder?: string
  disabled?: boolean
}>()

const emit = defineEmits([
  'update:modelValue',
  'focus',
])

defineExpose({
  focus: () => searchInputRef.value?.focus(),
  reset,
})

const syncSessionStore = useSyncSessionStore()
const stateRef = computed(() => syncSessionStore.getState(props.sessionId))

const placeholderRef = computed(() => props.placeholder ?? 'Type a name')
const selectedPersonRef = computed(() => getPerson(props.modelValue))

const showSearchInputRef = ref(false)
const selectorRef = ref<HTMLElement>()
const removeButtonRef = ref<HTMLButtonElement>()
const searchInputRef = ref<InstanceType<typeof SearchInput>>()

const { searchTextRef, searchResultsRef } = useLiveSearch<Person>(searchForPersonsAsync)

let stopClickOutside: (() => void) | undefined
onUnmounted(() => stopClickOutside?.())

function getPerson(id?: string) {
  return id ? stateRef.value?.sourceGraph?.persons.get(id) : undefined
}

async function searchForPersonsAsync(text: string) {
  const names = text.split(' ').map(n => n.trim().toLowerCase()).filter(n => n.length > 0)
  const persons = stateRef.value?.sourceGraph?.persons
  if (!persons) {
    return []
  }
  return [...persons.values()].filter(p => {
    const displayName = p.displayName?.toLowerCase() ?? ''
    return names.every(n => displayName.includes(n))
  })
}

async function searchResultSelected(id: string) {
  emit('update:modelValue', id)
  closeSearchInput()
  await nextTick()
  removeButtonRef.value?.focus()
}

async function onEdit(e: Event) {
  e.stopPropagation()
  showSearchInputRef.value = true
  await nextTick()
  searchInputRef.value?.focus()
  stopClickOutside?.()
  stopClickOutside = onClickOutside(selectorRef, () => closeSearchInput())
}

function closeSearchInput() {
  showSearchInputRef.value = false
  stopClickOutside?.()
  stopClickOutside = undefined
}

function reset() {
  searchTextRef.value = ''
  searchInputRef.value?.reset()
}
</script>
